import { useCallback, useState } from 'react';

import straightToCheckoutEnroll, {
  canStraightToCheckoutEnroll,
} from 'bundles/enroll-common/components/freemium-flow/utils/straightToCheckoutEnroll';
import type { EnrollmentChoices } from 'bundles/enroll-common/components/xdp/withEnrollment';

type Params = {
  courseId?: string;
  s12nId?: string;
};

/**
 * Straight-to-checkout enroll flow for the preview video CTA. Returns an `alternateEnrollFlow` that
 * skips the enroll modal for checkout when one can be built, plus an `isCheckoutLoading` flag for the
 * button's spinner (held until redirect, reset on failure). When no checkout can be built (e.g.
 * free/audit-only or unresolvable mix-and-match products), it returns `false` so the caller falls
 * back to the enroll modal instead of firing an invalid cart.
 */
export default function useStraightToCheckoutEnrollFlow({ courseId, s12nId }: Params) {
  const [isCheckoutLoading, setIsCheckoutLoading] = useState(false);

  const alternateEnrollFlow = useCallback<(enrollmentAvailableChoices?: EnrollmentChoices) => boolean>(
    (enrollmentAvailableChoices) => {
      if (!courseId || !canStraightToCheckoutEnroll({ courseId, s12nId, enrollmentAvailableChoices })) {
        return false; // fall back to the modal
      }
      setIsCheckoutLoading(true);
      straightToCheckoutEnroll({ courseId, s12nId, enrollmentAvailableChoices }).catch(() => {
        // On success the page redirects to checkout; only reset if the cart never got created.
        setIsCheckoutLoading(false);
      });
      return true;
    },
    [courseId, s12nId]
  );

  return { alternateEnrollFlow, isCheckoutLoading };
}
